import { selectHasExperienceVariants } from '@ninetailed/experience.js';

import type {
  ExperienceConfiguration,
  Profile,
  Reference,
  VariantRef
} from '@ninetailed/experience.js';

import {
  onMounted,
  onUnmounted,
  shallowRef,
  watch,
  type ShallowRef
} from 'vue';
import type { Entry } from 'contentful';
import { isContentPage } from '~/utils/guards/isContentPage';
import { sortExperiencesByAudiencePriority } from '~/utils/ninetailed/sortExperiencesByAudiencePriority';

type Load<TBaseline extends Reference> = {
  status: 'loading';
  loading: boolean;
  hasVariants: boolean;
  baseline: TBaseline;
  experience: null;
  variant: TBaseline;
  variantIndex: 0;
  audience: null;
  isPersonalized: boolean;
  profile: null;
  error: null;
};

type Success<TBaseline extends Reference, TVariant extends Reference> = {
  status: 'success';
  loading: boolean;
  hasVariants: boolean;
  baseline: TBaseline;
  experience: ExperienceConfiguration<TVariant> | null;
  variant: TVariant;
  variantIndex: number;
  audience: { id: string } | null;
  isPersonalized: boolean;
  profile: Profile;
  error: null;
};

type Fail<TBaseline extends Reference> = {
  status: 'error';
  loading: boolean;
  hasVariants: boolean;
  baseline: TBaseline;
  experience: null;
  variant: TBaseline;
  variantIndex: 0;
  audience: null;
  isPersonalized: boolean;
  profile: null;
  error: Error;
};

type UseExperienceArgs<
  TBaseline extends Reference,
  TVariant extends Reference
> = {
  baseline: TBaseline;
  experiences: ExperienceConfiguration<TVariant>[];
};

type UseExperienceReturn<
  TBaseline extends Reference,
  TVariant extends Reference
> =
  Load<TBaseline> | Success<TBaseline, TVariant | VariantRef> | Fail<TBaseline>;

export const useExperience = <
  TBaseline extends Reference,
  TVariant extends Reference
>({
  baseline,
  experiences
}: UseExperienceArgs<TBaseline, TVariant>): ShallowRef<
  UseExperienceReturn<TBaseline, TVariant>
> => {
  const {
    $ninetailed,
    $ninetailedAudiencePriority,
    $ninetailedStickyAudienceId
  } = useNuxtApp();

  // Order doesn't affect whether *any* variant exists, so this can use the
  // raw, unsorted experiences.
  const hasVariants = experiences
    .map((experience) => selectHasExperienceVariants(experience, baseline))
    .reduce((acc, curr) => acc || curr, false);

  const experience = shallowRef<
    UseExperienceReturn<TBaseline, TVariant | VariantRef>
  >({
    hasVariants,
    baseline,
    error: null,
    loading: true,
    status: 'loading',
    experience: null,
    variant: baseline,
    variantIndex: 0,
    audience: null,
    isPersonalized: false,
    profile: null
  });

  const isPage = isContentPage(baseline as unknown as Entry<unknown>);

  if (import.meta.server || isPage) {
    // We inject baseline content initially for SSR usage!
    return experience;
  }

  onMounted(() => {
    // Sort once at subscribe time. The SDK freezes that order for the
    // subscription lifetime — we deliberately do NOT re-subscribe when
    // sticky changes later (would stampede every Experience on the page).
    // Sticky still helps components that mount after the first winner.
    // Priority is available up-front from plugin 11 / audiences API.
    // `$ninetailed` is a Ref filled after the SDK chunk loads (non-blocking plugin).
    let unsubscribe: (() => void) | undefined;

    const stop = watch(
      $ninetailed,
      (ninetailed) => {
        if (!ninetailed) {
          return;
        }

        unsubscribe?.();

        const sortedExperiences = sortExperiencesByAudiencePriority(
          experiences,
          $ninetailedAudiencePriority?.value ?? {},
          $ninetailedStickyAudienceId?.value
        );

        unsubscribe = ninetailed.onSelectVariant(
          { baseline, experiences: sortedExperiences },
          (newExperienceState) => {
            experience.value = newExperienceState as UseExperienceReturn<
              TBaseline,
              TVariant
            >;

            // First component on the page to resolve a matched audience
            // "wins" it for later-mounting components (no re-subscribe stampede).
            const audienceId = (
              newExperienceState as { audience?: { id: string } | null }
            ).audience?.id;

            if (
              audienceId &&
              $ninetailedStickyAudienceId &&
              !$ninetailedStickyAudienceId.value
            ) {
              $ninetailedStickyAudienceId.value = audienceId;
            }
          }
        );
      },
      { immediate: true }
    );

    onUnmounted(() => {
      stop();
      unsubscribe?.();
    });
  });

  return experience;
};
